import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LuSquareCheck, LuSquare, LuArrowRight, LuCircleAlert, LuCircleHelp, LuCalculator } from 'react-icons/lu';
import { getCountryLabel } from '../../configs/countryLabels';
import { getDefaultTransportPremium } from '../../configs/transportPremium';


const useTaxShares = (formData, setFormData) => {
    const calculateTaxShares = useCallback(() => {
        let shares = 1;
        if (formData.maritalStatus === 'married') {
            shares = 2;
            if (formData.spouseIncome) {
                shares -= 0.5;
            }
        }

        const children = parseInt(formData.numberOfChildren) || 0; // <-- Move this up

        if (formData.maritalStatus === 'windower' && children > 0) {
            shares = 1.5;
        }

        if (children > 0) {
            shares += children * 0.5;
        }

        if (formData.foreign) {
            shares = Math.min(shares, 1.5);
        } else if (shares > 5) {
            shares = 5;
        }

        if (shares !== formData.taxShares) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                taxShares: shares
            }));
        }
    }, [formData, setFormData]);

    useEffect(() => {
        calculateTaxShares();
    }, [formData.maritalStatus, formData.spouseIncome, formData.numberOfChildren, formData.foreign, calculateTaxShares]);
};

const SalaryForm = ({ setServerResponse, setLoading, setCalculationType, calculationType, country }) => {
    const { t } = useTranslation();
    const [conventions, setConventions] = useState([]);
    const [formData, setFormData] = useState({
        convention: '',
        category: '',
        maritalStatus: '',
        spouseIncome: false,
        numberOfChildren: 0,
        additionalSalary: 0,
        brutSalary: 0,
        baseSalary: 0,
        netAmount: 0,
        taxShares: 0,
        foreign: false,
        executive: false,
        // Prime de transport préremplie avec le défaut du pays, modifiable ;
        // 0 est une valeur légitime (Yros/Support#39).
        transport_premium: getDefaultTransportPremium(country)
    });
    const [transportPremiumError, setTransportPremiumError] = useState('');

    const [categories, setCategories] = useState([]);
    const [formError, setFormError] = useState('');
    const [childrenFieldError, setChildrenFieldError] = useState('');
    const [brutSalaryError, setBrutSalaryError] = useState('');
    const [netAmountError, setNetAmountError] = useState('');

    // Turnstile : sitekey lue depuis l'env (REACT_APP_TURNSTILE_SITEKEY).
    // Vide = widget désactivé (pas de clé en dur).
    const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';
    const turnstileRef = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);

    // Animation variants
    const formVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" }
        }
    };

    const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://127.0.0.1:5003';
    // Utilise le pays pour l’URL
    const BACKEND_URL = `${API_URL}/simulateur/${country}`;

    useEffect(() => {
        setLoading(true);
        axios.get(`${BACKEND_URL}/api/payor`)
            .then(response => {
                setConventions(response.data.conventions || []);
                // Tu peux adapter ici selon le pays, ex: set d'autres champs
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des conventions', error);
            })
            .finally(() => setLoading(false));
    }, [setLoading, BACKEND_URL]);

    useEffect(() => {
        if (formData.convention) {
            setLoading(true);
            axios.get(`${BACKEND_URL}/api/payor/categories?convention_id=${formData.convention}`)
                .then(response => {
                    setCategories(response.data.categories || []);
                    // Adapter selon le pays si besoin
                })
                .catch(error => {
                    console.error("Error fetching categories:", error);
                    setCategories([]);
                    setFormError(t('simulatorForm.error_categories'));
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setCategories([]);
        }
    }, [formData.convention, setLoading, BACKEND_URL, t]);

    // Initialiser le widget Turnstile
    useEffect(() => {
        if (!TURNSTILE_SITEKEY) return; // captcha désactivé tant qu'aucune sitekey n'est configurée
        let mounted = true;
        const renderWidget = () => {
            if (!mounted) return;
            if (window.turnstile && turnstileRef.current && !turnstileWidgetId) {
                try {
                    const id = window.turnstile.render(turnstileRef.current, {
                        sitekey: TURNSTILE_SITEKEY,
                        callback: (token) => {
                            setTurnstileToken(token);
                        },
                        'error-callback': (code) => {
                            // Affiche le vrai code Cloudflare (ex: 110200 = domaine non autorisé)
                            console.error('Turnstile error (salary), code =', code);
                            return true; // évite l'erreur opaque « Script error. »
                        },
                        'expired-callback': () => setTurnstileToken('')
                    });
                    setTurnstileWidgetId(id);
                } catch (err) {
                    console.warn('Turnstile render failed:', err);
                }
            } else if (!window.turnstile) {
                // retry shortly until script loads
                setTimeout(renderWidget, 300);
            }
        };

        renderWidget();
        return () => { mounted = false; };
    }, [TURNSTILE_SITEKEY, turnstileRef, turnstileWidgetId]);

    const filteredCategories = categories;
    useTaxShares(formData, setFormData);

    const handleChange = (selectedOption, { name }) => {
        if (name === "convention") {
            setFormData({
                ...formData,
                convention: selectedOption ? selectedOption.value : '',
                category: '',
                baseSalary: 0,
                executive: false 
            });
        } else if (name === "category") {
            setFormData({
                ...formData,
                category: selectedOption ? selectedOption.value : '',
                baseSalary: selectedOption ? selectedOption.baseSalary : 0,
                executive: selectedOption ? selectedOption.is_executive : false 
            });
        } else {
            setFormData({
                ...formData,
                [name]: selectedOption ? selectedOption.value : ''
            });
        }
    };

    const handleChange2 = (e) => {
        const { name, value } = e.target;

        // Validation pour rejeter les montants négatifs pour brutSalary et netAmount
        if ((name === 'brutSalary' || name === 'netAmount')) {
            const errorSetter = name === 'brutSalary' ? setBrutSalaryError : setNetAmountError;
            
            if (value === '') {
                setFormData({
                    ...formData,
                    [name]: ''
                });
                errorSetter('');
                return;
            }

            const numValue = parseFloat(value);
            if (isNaN(numValue)) {
                errorSetter(t('simulatorForm.error_amount_invalid'));
                return;
            }

            if (numValue < 0) {
                errorSetter(
                    name === 'brutSalary'
                        ? t('simulatorForm.error_brut_negative')
                        : t('simulatorForm.error_net_negative')
                );
                return; // N'actualise PAS l'état du formulaire
            }

            // Valeur valide: mettre à jour l'état en tant que nombre
            errorSetter('');
            setFormData({
                ...formData,
                [name]: numValue
            });
            return;
        }
  
        // Validation pour le nombre d'enfants (entier, >=0, <=20)
        if (name === 'numberOfChildren') {
            if (value === '') {
                setFormData({
                    ...formData,
                    [name]: ''
                });
                setChildrenFieldError('');
                return;
            }

            const intValue = parseInt(value, 10);
            if (isNaN(intValue)) {
                setChildrenFieldError(t('simulatorForm.error_children_invalid'));
                return;
            }

            if (intValue < 0) {
                setChildrenFieldError(t('simulatorForm.error_children_negative'));
                return;
            }

            if (intValue > 20) {
                setChildrenFieldError(t('simulatorForm.error_children_max'));
                return;
            }

            setChildrenFieldError('');
            setFormData({
                ...formData,
                [name]: intValue
            });
            return;
        }

        // Prime de transport : montant positif ou nul. Vide, le backend
        // applique le défaut de la société.
        if (name === 'transport_premium') {
            if (value === '') {
                setFormData({ ...formData, [name]: '' });
                setTransportPremiumError('');
                return;
            }
            const numValue = parseFloat(value);
            if (isNaN(numValue) || numValue < 0) {
                setTransportPremiumError(t('simulatorForm.error_transport_bonus'));
                return;
            }
            setTransportPremiumError('');
            setFormData({ ...formData, [name]: numValue });
            return;
        }

        // Default: update other fields
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        // Validation supplémentaire avant envoi
        if (calculationType === 'net') {
            const brut = parseFloat(formData.brutSalary);
            if (isNaN(brut) || brut <= 0) {
                setFormError(t('simulatorForm.error_brut_required'));
                return;
            }
            if (brut < 0) {
                setFormError(t('simulatorForm.error_brut_negative'));
                return;
            }
        } else if (calculationType === 'brut') {
            const net = parseFloat(formData.netAmount);
            if (isNaN(net) || net <= 0) {
                setFormError(t('simulatorForm.error_net_required'));
                return;
            }
            if (net < 0) {
                setFormError(t('simulatorForm.error_net_negative'));
                return;
            }
        }

        setLoading(true);

        const updatedFormData = {
            ...formData,
            additionalSalary: calculationType === 'net' ? (parseFloat(formData.brutSalary) - formData.baseSalary) : 0,
            netAmount: calculationType === 'brut' ? formData.netAmount : 0,
            turnstileToken: turnstileToken || null
        };

        console.log("Form data:", updatedFormData);
        try {
            const response = await axios.post(`${BACKEND_URL}/api/payor/submit`, updatedFormData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            console.log("Server response:", response.data);
            const result = response.data.result;
            // On n'affiche le panneau de résultat que sur un succès explicite ;
            // toute autre réponse (erreur métier, erreur interne, format inattendu)
            // passe par l'encart d'erreur avec le message du backend si présent.
            if (!result || result.error || !result.success) {
                setFormError((result && result.error) || t('simulatorForm.error_calcul'));
                return;
            }
            setServerResponse(result);
        } catch (error) {
            console.error("Error submitting form:", error);
            // On n'écrit pas de résultat sur erreur : le panneau garde son état
            // (placeholder ou résultat précédent) au lieu d'afficher un détail à zéro.
            setFormError(t('simulatorForm.error_submit'));
        } finally {
            setLoading(false);
        }
    };

    const conventionOptions = conventions.map(convention => ({
        value: convention.id,
        label: convention.name
    }));

    const categoryOptions = filteredCategories.map(category => ({
        value: category.id,
        label: `${category.name} - Base salary: ${category.salary_for173h.toLocaleString()} FCFA`,
        baseSalary: category.salary_for173h,
        is_executive: category.is_executive,
    }));

    // Custom styles for react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '3rem',
            borderRadius: '0.75rem',
            borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
            boxShadow: state.isFocused ? '0 0 0 1px #f97316' : 'none',
            '&:hover': {
                borderColor: '#f97316'
            },
            padding: '0 0.25rem'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#ffedd5' : 'white',
            color: state.isSelected ? 'white' : '#374151',
            '&:active': {
                backgroundColor: '#f97316'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        })
    };

    const countryLabels = getCountryLabel(country);

    return (
        <motion.form 
            onSubmit={handleSubmit}
            className="space-y-4 w-full max-w-5xl mx-auto bg-white p-6"
            variants={formVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div variants={itemVariants} className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                    <LuCalculator className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{t('simulatorForm.form_title')}</h3>
            </motion.div>

            {/* Message pour Niger et Cameroun */}
            {(country === 'ne' || country === 'cm') && (
                <motion.div
                    variants={itemVariants}
                    className="bg-yellow-50 p-4 rounded-xl border border-yellow-200 text-yellow-700 flex items-center gap-3 mb-6"
                >
                    <LuCircleAlert className="w-5 h-5" />
                    <span>
                        {t('simulatorForm.developing_alert_line1', { country: country === 'ne' ? t('countries.ne') : t('countries.cm') })}<br />
                        {t('simulatorForm.developing_alert_line2')}
                    </span>
                </motion.div>
            )}

            {/* Formulaire normal si pays disponible */}
            {(country === 'sn' || country === 'ci') && (
                <>
                    {formError && (
                        <motion.div 
                            variants={itemVariants}
                            className="bg-red-50 p-4 rounded-xl border border-red-200 text-red-600 flex items-center gap-3"
                        >
                            <LuCircleAlert className="w-5 h-5" />
                            <span>{formError}</span>
                        </motion.div>
                    )}

                    <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                            <label htmlFor="formConvention" className="block text-sm font-medium text-gray-700">
                                {t('simulatorForm.label_convention')} <span className="text-red-500">*</span>
                            </label>
                            <Select
                                id="formConvention"
                                name="convention"
                                value={conventionOptions.find(option => option.value === formData.convention)}
                                onChange={handleChange}
                                options={conventionOptions}
                                placeholder={t('simulatorForm.placeholder_convention')}
                                required
                                styles={customSelectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="formCategory" className="block text-sm font-medium text-gray-700">
                                {t('simulatorForm.label_category')} <span className="text-red-500">*</span>
                            </label>
                            <Select
                                id="formCategory"
                                name="category"
                                value={categoryOptions.find(option => option.value === formData.category)}
                                onChange={handleChange}
                                options={categoryOptions}
                                placeholder={formData.convention ? t('simulatorForm.placeholder_category') : t('simulatorForm.placeholder_category_disabled')}
                                required
                                isDisabled={!formData.convention}
                                styles={customSelectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                            {formData.category && formData.baseSalary > 0 && (
                                <div className="mt-1 text-xs text-gray-500">
                                    {t('simulatorForm.base_salary_note', { amount: formData.baseSalary.toLocaleString() })}
                                </div>
                            )}
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                            <label htmlFor="formMaritalStatus" className="block text-sm font-medium text-gray-700">
                                {t('simulatorForm.label_marital_status')} <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="formMaritalStatus"
                                name="maritalStatus"
                                value={formData.maritalStatus}
                                onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                                required
                            >
                                <option value="">{t('simulatorForm.option_select')}</option>
                                <option value="single">{t('simulatorForm.option_single')}</option>
                                <option value="married">{t('simulatorForm.option_married')}</option>
                                <option value="widower">{t('simulatorForm.option_widower')}</option>
                                <option value="divorced">{t('simulatorForm.option_divorced')}</option>
                            </select>
                        </div>

                        {formData.maritalStatus === 'married' && (
                            <motion.div 
                                variants={itemVariants}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-1.5"
                            >
                                <label htmlFor="formSpouseIncome" className="block text-sm font-medium text-gray-700">
                                    {t('simulatorForm.label_spouse_income')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="formSpouseIncome"
                                    name="spouseIncome"
                                    value={formData.spouseIncome ? "yes" : "no"}
                                    onChange={(e) => setFormData({ ...formData, spouseIncome: e.target.value === "yes" })}
                                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                                    required
                                >
                                    <option value="no">{t('simulatorForm.option_no')}</option>
                                    <option value="yes">{t('simulatorForm.option_yes')}</option>
                                </select>
                            </motion.div>
                        )}
                    </motion.div>

                    <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                            <label htmlFor="formNumberOfChildren" className="block text-sm font-medium text-gray-700">
                                {t('simulatorForm.label_number_of_children')}
                            </label>
                            <input
                                id="formNumberOfChildren"
                                type="number"
                                min="0"
                                max="20"
                                step="1"
                                name="numberOfChildren"
                                value={formData.numberOfChildren}
                                onChange={handleChange2}
                                className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                                    childrenFieldError
                                        ? 'border-red-500 focus:ring-red-400'
                                        : 'border-gray-300 focus:ring-orange-400'
                                }`}
                            />
                            {childrenFieldError && (
                                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                                    <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                                    {childrenFieldError}
                                </p>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-gray-700">
                                {t(countryLabels.taxSharesLabelKey)}
                            </label>
                            <input
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none transition-all cursor-not-allowed"
                                value={formData.taxShares}
                                readOnly
                            />
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                            <label htmlFor="formCalculationType" className="block text-sm font-medium text-gray-700">
                                {t('simulatorForm.label_calculation_type')} <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="formCalculationType"
                                name="calculationType"
                                value={calculationType}
                                onChange={(e) => setCalculationType(e.target.value)}
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                                required
                            >
                                <option value="net">{t('simulatorForm.option_calc_net')}</option>
                                <option value="brut">{t('simulatorForm.option_calc_brut')}</option>
                            </select>
                        </div>

                        {calculationType === 'net' ? (
                            <div className="space-y-1.5">
                                <label htmlFor="formBrutSalary" className="block text-sm font-medium text-gray-700">
                                    {t('simulatorForm.label_brut_salary')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        id="formBrutSalary"
                                        type="number"
                                        name="brutSalary"
                                        value={formData.brutSalary}
                                        onChange={handleChange2}
                                        min="0"
                                        step="0.01"
                                        className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all pr-16 ${
                                            brutSalaryError
                                                ? 'border-red-500 focus:ring-red-400'
                                                : 'border-gray-300 focus:ring-orange-400'
                                        }`}
                                        placeholder={t('simulatorForm.placeholder_amount')}
                                        required
                                    />
                                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">FCFA</span>
                                </div>
                                {brutSalaryError && (
                                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                                        <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                                        {brutSalaryError}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                <label htmlFor="formNetAmount" className="block text-sm font-medium text-gray-700">
                                    {t('simulatorForm.label_net_amount')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        id="formNetAmount"
                                        type="number"
                                        name="netAmount"
                                        value={formData.netAmount}
                                        onChange={handleChange2}
                                        min="0"
                                        step="0.01"
                                        className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all pr-16 ${
                                            netAmountError
                                                ? 'border-red-500 focus:ring-red-400'
                                                : 'border-gray-300 focus:ring-orange-400'
                                        }`}
                                        placeholder={t('simulatorForm.placeholder_amount')}
                                        required
                                    />
                                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">FCFA</span>
                                </div>
                                {netAmountError && (
                                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                                        <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
                                        {netAmountError}
                                    </p>
                                )}
                            </div>
                        )}
                    </motion.div>

                    <motion.div variants={itemVariants} className="pt-4">
                        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                            <h4 className="text-base font-medium text-gray-700 mb-4">{t('simulatorForm.additional_options_heading')}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    {formData.executive ? (
                                        <LuSquareCheck className="w-6 h-6 text-orange-500" />
                                    ) : (
                                        <LuSquare className="w-6 h-6 text-gray-400" />
                                    )}
                                    <input
                                        id="formCadre"
                                        type="checkbox"
                                        name="executive"
                                        checked={formData.executive}
                                        onChange={(e) => setFormData({ ...formData, executive: e.target.checked })}
                                        className="hidden"
                                    />
                                    <div>
                                        <span className="font-medium text-gray-900">{t('simulatorForm.executive_label')}</span>
                                        <p className="text-sm text-gray-500">{t('simulatorForm.executive_desc')}</p>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    {formData.foreign ? (
                                        <LuSquareCheck className="w-6 h-6 text-orange-500" />
                                    ) : (
                                        <LuSquare className="w-6 h-6 text-gray-400" />
                                    )}
                                    <input
                                        id="formForeign"
                                        type="checkbox"
                                        name="foreign"
                                        checked={formData.foreign}
                                        onChange={(e) => setFormData({ ...formData, foreign: e.target.checked })}
                                        className="hidden"
                                    />
                                    <div>
                                        <span className="font-medium text-gray-900">{t('simulatorForm.foreign_label')}</span>
                                        <p className="text-sm text-gray-500">{t('simulatorForm.foreign_desc')}</p>
                                    </div>
                                </label>

                                <div className="space-y-1.5">
                                    <label htmlFor="transport_premium" className="block font-medium text-gray-900">
                                        {t('simulatorForm.label_transport_bonus')}
                                    </label>
                                    <input
                                        id="transport_premium"
                                        type="number"
                                        min={0}
                                        name="transport_premium"
                                        value={formData.transport_premium}
                                        onChange={handleChange2}
                                        placeholder={String(getDefaultTransportPremium(country))}
                                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                                    />
                                    {transportPremiumError && (
                                        <p className="text-sm text-red-600 flex items-center gap-2">
                                            <LuCircleAlert className="w-4 h-4 flex-shrink-0" />
                                            {transportPremiumError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div variants={itemVariants} className="pt-4">
                        {TURNSTILE_SITEKEY && (
                            <div className="mb-4">
                                <div ref={turnstileRef} id="turnstile-salary" />
                            </div>
                        )}
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            {t('simulatorForm.submit')}
                            <LuArrowRight className="w-5 h-5" />
                        </motion.button>

                        <p className="text-xs text-center text-gray-500 mt-4">
                            {t('simulatorForm.legal_note')}
                        </p>
                    </motion.div>

                    <motion.div variants={itemVariants} className="pt-4 mt-auto">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <div className="p-3 bg-white rounded-full shadow-sm">
                                    <LuCircleHelp className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-gray-700 mb-3 text-center sm:text-left">
                                        {t('simulatorForm.help_note')}
                                    </p>
                                    <div className="flex justify-center sm:justify-start">
                                        <a
                                            href="/contact"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:shadow-md transition-all group"
                                        >
                                            {t('simulatorForm.help_cta')}
                                            <LuArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </motion.form>
    );
};

export default SalaryForm;

